import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, RefreshCw } from 'lucide-react';

interface ContentNotFoundProps {
  contentType?: string;
  contentId?: string;
  error?: string;
  onRetry?: () => void;
}

const ContentNotFound: React.FC<ContentNotFoundProps> = ({
  contentType = 'content',
  contentId,
  error,
  onRetry
}) => {
  const navigate = useNavigate();

  const getCategoryPage = () => {
    switch (contentType) {
      case 'podcast':
        return '/podcasts';
      case 'animation':
        return '/animations';
      case 'cinema':
        return '/media/cinema';  
      case 'content':
        return '/media/contents';
      case 'sneak-peek':
        return '/sneak-peeks';
      case 'series':
        return '/originals'; // Series are typically featured in originals
      default:
        return '/';
    }
  };

  const getCategoryName = () => {
    switch (contentType) {
      case 'podcast':
        return 'Podcasts';
      case 'animation':
        return 'Animations';
      case 'cinema':
        return 'Cinema';
      case 'content':
        return 'Content';
      case 'sneak-peek':
        return 'Sneak Peeks';
      case 'series':
        return 'Series';
      default:
        return 'Browse';
    }
  };

  const handleGoToCategory = () => {
    navigate(getCategoryPage());
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Error Icon */}
        <div className="mx-auto w-24 h-24 mb-8 rounded-full bg-red-500/10 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
            <span className="text-red-400 text-2xl">❌</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-4">
          Content Not Found
        </h1>

        {/* Description */}
        <div className="text-gray-300 mb-6 space-y-2">
          <p>
            The {contentType} content you're looking for doesn't exist or may have been removed.
          </p>
          
          {contentId && (
            <p className="text-sm text-gray-400 font-mono bg-gray-800 p-2 rounded break-all">
              ID: {contentId}
            </p>
          )}

          {error && error !== 'Content not found' && (
            <details className="text-sm text-gray-400 bg-gray-800 p-3 rounded text-left">
              <summary className="cursor-pointer hover:text-gray-300">
                Technical Details
              </summary>
              <div className="mt-2 font-mono text-xs">
                {error}
              </div>
            </details>
          )}
        </div>

        {/* Possible Causes */}
        <div className="text-sm text-gray-400 mb-8 text-left bg-gray-800/50 p-4 rounded">
          <p className="font-semibold text-gray-300 mb-2">This might happen if:</p>
          <ul className="space-y-1 text-xs">
            <li>• The content was deleted or moved</li>
            <li>• You followed an invalid or outdated link</li>
            <li>• There was an error in content navigation</li>
            <li>• The content is temporarily unavailable</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {onRetry && (
            <button
              onClick={onRetry}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          )}

          <button
            onClick={handleGoToCategory}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <span>Browse {getCategoryName()}</span>
          </button>

          <div className="flex gap-3">
            <button
              onClick={handleGoBack}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} />
              Go Back
            </button>

            <button
              onClick={handleGoHome}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Home size={16} />
              Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentNotFound;